/**
 * Small counts, in words — the panel's own copy vocabulary (issues #303, #329).
 *
 * Both screens the canvas draws spell their counts: *"Everything I've saved about your family —
 * **sixteen** things in all"* on `Memory.dc.html`, *"**Four** of you, and **one** invitation
 * nobody has answered yet"* on `Roster.dc.html`. That is the product's voice rather than a
 * formatting preference: the sentence is Boone talking, and Boone does not say "4 of you".
 *
 * **One table because two would drift**, which is the argument `contextNouns.ts` already made
 * and won inside the service: the roster and the memory screen would each have grown a list,
 * one of them would have been extended, and a family would meet *"sixteen things"* on one page
 * and *"16 of you"* on the other.
 *
 * It stays in this app rather than in `@pilotfish/core`, whose rule is *words more than one
 * runtime must agree on*. Nothing else says these. If a second surface ever does, the move is
 * the one {@link CONTEXT_KIND_HEADING} already made.
 */

/**
 * Where spelling stops.
 *
 * Twenty, because that is where written English stops spelling by default and because the two
 * numbers this actually renders — a roster bounded at six (#251) and a family's durable record,
 * which the canvas draws at sixteen — are both well inside it. Past it, digits: *"a hundred and
 * forty-three things in all"* is not warmer than "143", it is harder to read.
 */
const WORDS = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "eleven",
  "twelve",
  "thirteen",
  "fourteen",
  "fifteen",
  "sixteen",
  "seventeen",
  "eighteen",
  "nineteen",
  "twenty",
] as const;

/** `4` → `"four"`, `143` → `"143"`. Lowercase; the caller decides where a sentence starts. */
export function spell(n: number): string {
  return WORDS[n] ?? n.toLocaleString();
}

/** `"four of you"` → `"Four of you"`. */
export function sentenceCase(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
